const coreExerciseKey = (ref) => `${ref.benchmarkGridId}:${ref.libraryExerciseId}`;
/**
 * Keeps only benchmarks whose (benchmarkGridId, libraryExerciseId) is still a
 * core exercise of that grid. When a coach removes a core exercise the recorded
 * AthleteExerciseBenchmark row is intentionally left in the database (so it can
 * be restored by re-adding the exercise), but it must no longer drive workout %
 * conversion - dropping it here makes applyBenchmarkDataToIntensity find no
 * value and leave the prescribed percentage unchanged.
 */
export const filterBenchmarksToCoreExercises = (benchmarks, coreExercises) => {
    const coreExerciseKeys = new Set(coreExercises.map(coreExerciseKey));
    return benchmarks.filter((benchmark) => coreExerciseKeys.has(coreExerciseKey(benchmark)));
};
export function applyBenchmarkDataToIntensity(intensity, libraryExerciseId, applyBenchmarkData, benchmarkGridId, profileId) {
    // If a benchmark grid id is provided, use it; Otherwise use the default benchmark grid id
    const benchmarkToUse = benchmarkGridId ?? applyBenchmarkData.defaultBenchmarkGridId;
    const exerciseBenchmarksForGrid = applyBenchmarkData.currentExerciseBenchmarks.filter((benchmark) => benchmark.benchmarkGridId === benchmarkToUse);
    return applyAthleteExerciseBenchmarksToIntensity(intensity, libraryExerciseId, exerciseBenchmarksForGrid, applyBenchmarkData.maxConversions, applyBenchmarkData.trainingMaxRatio, profileId);
}
function applyAthleteExerciseBenchmarksToIntensity(intensity, libraryExerciseId, currentBenchmarks, conversions = [], multiplier = 1, profileId) {
    // Only percentage intensities are scaled here. Empty, keyword, already-resolved
    // weight, and already-resolved KPI intensities pass through unchanged (the
    // weight passthrough behavior is locked by the characterization suite). The
    // keyword variant has no `format` field at all, so it's excluded explicitly
    // before checking `.format` on the rest of the union.
    if (intensity === '' ||
        intensity.type === 'keyword' ||
        intensity.format !== 'percentage') {
        return intensity;
    }
    const benchmarkValue = benchmarkValueForExercise(libraryExerciseId, currentBenchmarks, conversions, multiplier, profileId);
    if (!benchmarkValue) {
        return intensity;
    }
    return scaleAndRoundIntensityBenchmark(intensity, benchmarkValue);
}
function scaleAndRoundIntensityBenchmark(intensity, benchmarkValue) {
    if (intensity.type === 'exact') {
        const rawValue = (benchmarkValue.value * intensity.value) / 100;
        const roundTo = benchmarkValue.unit === 'lbs' ? 5 : 2.5;
        const value = Math.round(rawValue / roundTo) * roundTo;
        return {
            format: 'weight',
            type: 'exact',
            value,
            unit: benchmarkValue.unit,
        };
    }
    else {
        const minRawValue = (benchmarkValue.value * intensity.min) / 100;
        const maxRawValue = (benchmarkValue.value * intensity.max) / 100;
        const roundTo = benchmarkValue.unit === 'lbs' ? 5 : 2.5;
        const minValue = Math.round(minRawValue / roundTo) * roundTo;
        const maxValue = Math.round(maxRawValue / roundTo) * roundTo;
        return {
            format: 'weight',
            type: 'range',
            min: minValue,
            max: maxValue,
            unit: benchmarkValue.unit,
        };
    }
}
function benchmarkValueForExercise(libraryExerciseId, currentBenchmarks, conversions, multiplier, profileId) {
    const matchingBenchmark = currentBenchmarks?.find((b) => {
        return (b.libraryExerciseId === libraryExerciseId &&
            (!profileId || b.profileId === profileId));
    });
    if (matchingBenchmark) {
        return {
            value: matchingBenchmark.value * multiplier,
            unit: matchingBenchmark.unit,
        };
    }
    for (const conversion of conversions) {
        if (conversion.toLibraryExerciseId !== libraryExerciseId) {
            continue;
        }
        const conversionMatchingBenchmark = currentBenchmarks?.find((b) => b.libraryExerciseId === conversion.fromLibraryExerciseId &&
            (!profileId || b.profileId === profileId));
        if (conversionMatchingBenchmark) {
            return {
                value: conversionMatchingBenchmark.value * multiplier * conversion.ratio,
                unit: conversionMatchingBenchmark.unit,
            };
        }
    }
    return null;
}
//# sourceMappingURL=intensity.js.map